<template>
  <div>
    <div class="page-toolbar">
      <div class="page-title">品种管理</div>
      <div class="toolbar-actions">
        <el-input
          v-model.trim="keyword"
          clearable
          placeholder="按品种名称/ID搜索"
          style="width: 240px;"
          @input="handleFilterChange"
        />
        <el-button icon="el-icon-refresh" @click="loadVarieties">刷新</el-button>
        <el-button type="primary" icon="el-icon-plus" @click="openCreateDialog">新增品种</el-button>
      </div>
    </div>

    <el-card class="page-card">
      <el-table :data="pagedVarieties" v-loading="loading" stripe>
        <el-table-column type="index" :index="paginationIndex" width="56" label="#" />
        <el-table-column prop="id" label="品种ID" min-width="220" show-overflow-tooltip />
        <el-table-column prop="name" label="品种名称" min-width="180" />
        <el-table-column prop="createdAt" label="创建时间" width="180" />
        <el-table-column label="操作" width="120" fixed="right">
          <template slot-scope="{ row }">
            <el-button size="mini" type="danger" @click="removeVariety(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="list-pagination">
        <el-pagination
          background
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="pageSizes"
          :total="filteredVarieties.length"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handlePageSizeChange"
          @current-change="handleCurrentPageChange"
        />
      </div>
    </el-card>

    <el-dialog title="新增品种" :visible.sync="dialogVisible" width="420px" @closed="resetForm">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="80px">
        <el-form-item label="品种名称" prop="name">
          <el-input
            v-model.trim="form.name"
            maxlength="80"
            show-word-limit
            placeholder="请输入品种名称"
            @keyup.enter.native="submitForm"
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { createVariety, deleteVariety, fetchVarieties } from '../../api/admin'
import { isAuthRedirectError } from '../../utils/auth'

export default {
  name: 'VarietyListView',
  data() {
    return {
      loading: false,
      saving: false,
      dialogVisible: false,
      varieties: [],
      keyword: '',
      currentPage: 1,
      pageSize: 10,
      pageSizes: [10, 20, 50, 100],
      form: {
        name: ''
      },
      rules: {
        name: [
          { required: true, message: '请输入品种名称', trigger: 'blur' },
          { validator: this.validateUniqueName, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    filteredVarieties() {
      const keyword = String(this.keyword || '').trim().toLowerCase()
      if (!keyword) return this.varieties
      return this.varieties.filter((item) => [item && item.name, item && item.id]
        .map(value => String(value || '').toLowerCase())
        .some(value => value.includes(keyword)))
    },
    pagedVarieties() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filteredVarieties.slice(start, start + this.pageSize)
    }
  },
  created() {
    this.loadVarieties()
  },
  methods: {
    normalizeVarietyName(value) {
      return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase()
    },
    validateUniqueName(rule, value, callback) {
      const normalizedName = this.normalizeVarietyName(value)
      if (!normalizedName) {
        callback()
        return
      }
      const duplicated = this.varieties.some(item => this.normalizeVarietyName(item && item.name) === normalizedName)
      if (duplicated) {
        callback(new Error('品种名称已存在，请勿重复添加'))
        return
      }
      callback()
    },
    paginationIndex(index) {
      return (this.currentPage - 1) * this.pageSize + index + 1
    },
    handleFilterChange() {
      this.currentPage = 1
    },
    handlePageSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
    },
    handleCurrentPageChange(page) {
      this.currentPage = page
    },
    adjustCurrentPage() {
      const maxPage = Math.max(1, Math.ceil(this.filteredVarieties.length / this.pageSize))
      this.currentPage = Math.min(this.currentPage, maxPage)
    },
    async loadVarieties() {
      this.loading = true
      try {
        const list = await fetchVarieties()
        this.varieties = Array.isArray(list) ? list : []
        this.adjustCurrentPage()
      } catch (error) {
        if (isAuthRedirectError(error)) return
        this.$message.error(error.message || '品种列表获取失败')
      } finally {
        this.loading = false
      }
    },
    openCreateDialog() {
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.clearValidate()
      })
    },
    resetForm() {
      this.form = { name: '' }
      if (this.$refs.formRef) this.$refs.formRef.clearValidate()
    },
    submitForm() {
      if (!this.$refs.formRef) return
      this.$refs.formRef.validate(async (valid) => {
        if (!valid) return
        this.saving = true
        try {
          await createVariety({ name: this.form.name })
          this.$message.success('品种已创建')
          this.dialogVisible = false
          await this.loadVarieties()
        } catch (error) {
          if (isAuthRedirectError(error)) return
          this.$message.error(error.message || '品种创建失败')
        } finally {
          this.saving = false
        }
      })
    },
    removeVariety(row) {
      this.$confirm(`确认删除品种“${row.name || '-'}”吗？`, '提示', { type: 'warning' })
        .then(async () => {
          await deleteVariety(row.id)
          this.$message.success('删除成功')
          await this.loadVarieties()
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.toolbar-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.list-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}
</style>
